<template>
  <div class="business-schedule-day">
    <div class="day-header">
      <h5 class="day-name">{{ dayLabel }}</h5>
      <button
        v-if="!isEditing"
        class="edit-btn"
        @click="startEditing"
        :disabled="isLoading"
      >
        <i class="icon-pencil"></i>
      </button>
    </div>

    <div class="day-schedule">
      <div v-if="!isEditing" class="view-mode">
        <div class="period">
          <span class="period-label">Manhã</span>
          <span class="period-time">{{ formatPeriod(schedule.am) }}</span>
        </div>

        <div class="period">
          <span class="period-label">Tarde</span>
          <span class="period-time">{{ formatPeriod(schedule.pm) }}</span>
        </div>
      </div>

      <div v-else class="edit-mode">
        <div class="period-edit">
          <div class="period-header">
            <span class="period-label">Manhã</span>
            <button
              v-if="localSchedule.am.start || localSchedule.am.end"
              class="clear-btn"
              @click="clearPeriod('am')"
              type="button"
            >
              <i class="icon-trash"></i>
            </button>
          </div>
          <div class="time-inputs">
            <input-field-component
              class="time-input"
              placeholder="--:--"
              type="time"
              inline
              compact
              :validHours="amStartValidHours"
              v-model="localSchedule.am.start"
              @input="validateAmStart"
            />
            <span class="separator">às</span>
            <input-field-component
              class="time-input"
              placeholder="--:--"
              type="time"
              inline
              compact
              :validHours="amEndValidHours"
              v-model="localSchedule.am.end"
              @input="validateAmEnd"
            />
          </div>
        </div>

        <div class="period-edit">
          <div class="period-header">
            <span class="period-label">Tarde</span>
            <button
              v-if="localSchedule.pm.start || localSchedule.pm.end"
              class="clear-btn"
              @click="clearPeriod('pm')"
              type="button"
            >
              <i class="icon-trash"></i>
            </button>
          </div>
          <div class="time-inputs">
            <input-field-component
              class="time-input"
              placeholder="--:--"
              type="time"
              inline
              compact
              :validHours="pmStartValidHours"
              v-model="localSchedule.pm.start"
              @input="validatePmStart"
            />
            <span class="separator">às</span>
            <input-field-component
              class="time-input"
              placeholder="--:--"
              type="time"
              inline
              compact
              :validHours="pmEndValidHours"
              v-model="localSchedule.pm.end"
              @input="validatePmEnd"
            />
          </div>
        </div>

        <div class="action-buttons">
          <button-component
            text="Salvar"
            @click.native="saveChanges"
            :disabled="isLoading"
          />
          <button-component
            text="Cancelar"
            @click.native="cancelEditing"
            :disabled="isLoading"
            styles="ghost"
          />
        </div>

        <div v-if="validationError" class="validation-error">
          {{ validationError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import InputDateField from '@/components/Base/InputDateField';
  import Button from '@/components/Base/Button';

  export default {
    name: 'BusinessScheduleDay',

    components: {
      'input-field-component': InputDateField,
      'button-component': Button
    },

    props: {
      dayName: {
        type: String,
        required: true
      },
      schedule: {
        type: Object,
        required: true,
        default: () => ({
          am: { start: null, end: null },
          pm: { start: null, end: null }
        })
      },
      isLoading: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        isEditing: false,
        localSchedule: {
          am: { start: null, end: null },
          pm: { start: null, end: null }
        },
        validationError: null
      };
    },

    computed: {
      dayLabel() {
        const dayLabels = {
          monday: 'Segunda',
          tuesday: 'Terça',
          wednesday: 'Quarta',
          thursday: 'Quinta',
          friday: 'Sexta',
          saturday: 'Sábado',
          sunday: 'Domingo'
        };
        return dayLabels[this.dayName] || this.dayName;
      }
    },

    watch: {
      schedule: {
        handler(newSchedule) {
          this.localSchedule = JSON.parse(JSON.stringify(newSchedule));
        },
        deep: true,
        immediate: true
      }
    },

    methods: {
      formatPeriod(period) {
        if (!period || !period.start || !period.end) {
          return 'Fechado';
        }
        return `${period.start} - ${period.end}`;
      },

      startEditing() {
        this.isEditing = true;
        this.localSchedule = JSON.parse(JSON.stringify(this.schedule));
        this.validationError = null;
      },

      cancelEditing() {
        this.isEditing = false;
        this.localSchedule = JSON.parse(JSON.stringify(this.schedule));
        this.validationError = null;
      },

      validateAmStart(value) {
        this.validationError = null;
        if (value && this.localSchedule.am.end) {
          if (value >= this.localSchedule.am.end) {
            this.validationError =
              'Horário de início da manhã deve ser menor que o de fim';
          }
        }
        this.validateOverlap();
      },

      validateAmEnd(value) {
        this.validationError = null;
        if (value && this.localSchedule.am.start) {
          if (value <= this.localSchedule.am.start) {
            this.validationError =
              'Horário de fim da manhã deve ser maior que o de início';
          }
        }
        this.validateOverlap();
      },

      validatePmStart(value) {
        this.validationError = null;
        if (value && this.localSchedule.pm.end) {
          if (value >= this.localSchedule.pm.end) {
            this.validationError =
              'Horário de início da tarde deve ser menor que o de fim';
          }
        }
        this.validateOverlap();
      },

      validatePmEnd(value) {
        this.validationError = null;
        if (value && this.localSchedule.pm.start) {
          if (value <= this.localSchedule.pm.start) {
            this.validationError =
              'Horário de fim da tarde deve ser maior que o de início';
          }
        }
        this.validateOverlap();
      },

      validateOverlap() {
        if (this.localSchedule.am.end && this.localSchedule.pm.start) {
          if (this.localSchedule.am.end > this.localSchedule.pm.start) {
            this.validationError =
              'Horários da manhã e tarde não podem se sobrepor';
          }
        }
      },

      validateSchedule() {
        this.validationError = null;

        // Validar se os campos estão completos quando preenchidos
        if (
          (this.localSchedule.am.start && !this.localSchedule.am.end) ||
          (!this.localSchedule.am.start && this.localSchedule.am.end)
        ) {
          this.validationError =
            'Informe tanto horário de início quanto de fim para a manhã';
          return false;
        }

        if (
          (this.localSchedule.pm.start && !this.localSchedule.pm.end) ||
          (!this.localSchedule.pm.start && this.localSchedule.pm.end)
        ) {
          this.validationError =
            'Informe tanto horário de início quanto de fim para a tarde';
          return false;
        }

        // Todas as outras validações já estão sendo feitas em tempo real
        return this.validationError === null;
      },

      saveChanges() {
        console.log('localSchedule antes de salvar:', this.localSchedule);
        
        if (this.validateSchedule()) {
          const cleanedSchedule = {
            am: {
              start: this.localSchedule.am.start || null,
              end: this.localSchedule.am.end || null
            },
            pm: {
              start: this.localSchedule.pm.start || null,
              end: this.localSchedule.pm.end || null
            }
          };

          console.log('cleanedSchedule a ser enviado:', cleanedSchedule);
          this.$emit('updateSchedule', this.dayName, cleanedSchedule);
          this.isEditing = false;
        }
      },

      clearPeriod(period) {
        this.localSchedule[period] = { start: null, end: null };
        this.validationError = null;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .business-schedule-day {
    border: 1px solid #dadada;
    border-radius: 4px;
    background: white;
    overflow: hidden;
    width: 100%;

    .day-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f5f5f5;
      padding: 0.4rem 0.5rem;
      border-bottom: 1px solid #dadada;

      .day-name {
        font-size: 13px;
        font-weight: 600;
        color: #333;
        margin: 0;
      }

      .edit-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 2px;
        color: #0052cc;

        &:hover:not(:disabled) {
          background: #e6f3ff;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        i {
          font-size: 12px;
        }
      }
    }

    .day-schedule {
      padding: 0.4rem;

      .view-mode {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.3rem;

        .period {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.3rem 0.4rem;
          background: #fafafa;
          border-radius: 3px;
          border: 1px solid #eee;

          .period-label {
            font-size: 11px;
            color: #666;
            font-weight: 600;
            min-width: 45px;
          }

          .period-time {
            font-size: 13px;
            color: #333;
            font-weight: 600;
            text-align: right;
            flex: 1;
          }
        }
      }

      .edit-mode {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;

        .period-edit {
          padding: 0.3rem 0.4rem;
          background: #fafafa;
          border-radius: 3px;
          border: 1px solid #eee;

          .period-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.3rem;

            .period-label {
              font-size: 11px;
              color: #666;
              font-weight: 600;
            }

            .clear-btn {
              background: none;
              border: none;
              cursor: pointer;
              padding: 0.25rem;
              border-radius: 2px;
              color: #0052cc;

              &:hover:not(:disabled) {
                background: #e6f3ff;
              }

              &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
              }

              i {
                font-size: 12px;
              }
            }
          }

          .time-inputs {
            display: flex;
            align-items: center;
            gap: 0.3rem;

            .time-input {
              flex: 1;
              min-width: 0;
              max-width: 120px;
            }

            .separator {
              font-size: 11px;
              color: #666;
              font-weight: 500;
              white-space: nowrap;
            }
          }
        }

        .action-buttons {
          display: flex;
          gap: 0.4rem;
          justify-content: end;
          margin-top: 0.3rem;

          .button-component {
            background: none;
            border: 1px solid;
            cursor: pointer;
            padding: 0.3rem 0.6rem;
            border-radius: 3px;
            font-size: 11px;

            i {
              font-size: 11px;
            }
          }

          .save-btn {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            color: #08b978;
            border-color: #08b978;

            &:hover:not(:disabled) {
              background: #e8f5e8;
            }
          }

          .cancel-btn {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            color: #ff5a5a;
            border-color: #ff5a5a;

            &:hover:not(:disabled) {
              background: #ffe8e8;
            }
          }
        }

        .validation-error {
          margin-top: 0.3rem;
          padding: 0.3rem;
          background: #ffe8e8;
          color: #ff5a5a;
          border-radius: 3px;
          font-size: 10px;
          text-align: center;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .business-schedule-day {
      .day-header {
        .day-name {
          font-size: 14px;
        }
      }

      .day-schedule {
        .view-mode {
          .period {
            .period-label {
              font-size: 12px;
            }

            .period-time {
              font-size: 14px;
            }
          }
        }
      }
    }
  }
</style>
